/**
 * PageSkeletons.jsx — shimmer-loader placeholders for the three highest-traffic
 * manager pages. Replaces the spinner+"Loading..." UI that flashes for ~200ms
 * before data lands. Renders the eventual shape of the page (header, KPI cards,
 * table rows) with a shimmering gradient where text/numbers will be — so the
 * user's eye settles on the final layout instantly, not on a moving spinner.
 *
 * Why these shapes specifically:
 *   • TenantsSkeleton — 4 KPI cards in a 2-col grid (matches the live page)
 *   • CollectionsSkeleton — KPI row + table of 6 placeholder rows
 *   • HomeSkeleton — greeting line + 4 KPIs + activity feed shape
 *
 * The shimmer effect uses Tailwind's animate-pulse with a gradient overlay so
 * it works without an extra CSS keyframe. ~20 lines per skeleton.
 */
import React from 'react';

// Single bar — width/height controlled by caller. Used as the "text" element.
const Bar = ({ w = 'w-24', h = 'h-4', className = '' }) => (
  <div className={`${w} ${h} bg-gray-200 rounded ${className}`} />
);

// One rectangle KPI card — same shape as the live KPI cards on Tenants/Home.
const KpiCardSkeleton = ({ accentClass = 'border-l-blue-400' }) => (
  <div className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${accentClass} flex items-center justify-between`}>
    <div className="space-y-2">
      <Bar w="w-20" h="h-3" />
      <Bar w="w-14" h="h-7" />
    </div>
    <div className="w-10 h-10 rounded-full bg-gray-200" />
  </div>
);

// Single row of a list — Avatar + 2 lines of "text" + chevron
const RowSkeleton = () => (
  <div className="bg-white rounded-xl p-4 flex items-center gap-3 shadow-sm">
    <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <Bar w="w-1/2" h="h-3" />
      <Bar w="w-1/3" h="h-3" />
    </div>
    <div className="w-12 h-6 rounded-full bg-gray-200" />
  </div>
);

const SkeletonWrap = ({ children, testId }) => (
  <div className="p-4 sm:p-6 space-y-4 animate-pulse" data-testid={testId}>
    {children}
  </div>
);

// ─── Tenants page ─────────────────────────────────────────────────────────
export const TenantsSkeleton = () => (
  <SkeletonWrap testId="tenants-skeleton">
    {/* Header */}
    <div className="flex items-start justify-between flex-wrap gap-2">
      <div className="space-y-2">
        <Bar w="w-32" h="h-7" />
        <Bar w="w-48" h="h-3" />
      </div>
      <div className="w-32 h-9 rounded-lg bg-gray-200" />
    </div>
    {/* KPI grid */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <KpiCardSkeleton accentClass="border-l-blue-400" />
      <KpiCardSkeleton accentClass="border-l-green-400" />
      <KpiCardSkeleton accentClass="border-l-purple-400" />
      <KpiCardSkeleton accentClass="border-l-orange-400" />
    </div>
    {/* Search + filter pills */}
    <div className="flex gap-2 flex-wrap">
      <div className="flex-1 min-w-[160px] h-10 rounded-lg bg-gray-100" />
      <div className="w-20 h-10 rounded-lg bg-gray-100" />
      <div className="w-20 h-10 rounded-lg bg-gray-100" />
    </div>
    {/* List rows */}
    <div className="space-y-2">
      {[0, 1, 2, 3, 4, 5].map((i) => <RowSkeleton key={i} />)}
    </div>
  </SkeletonWrap>
);

// ─── Collections / Money page ─────────────────────────────────────────────
export const CollectionsSkeleton = () => (
  <SkeletonWrap testId="collections-skeleton">
    <div className="flex items-start justify-between flex-wrap gap-2">
      <div className="space-y-2">
        <Bar w="w-28" h="h-6" />
        <Bar w="w-56" h="h-3" />
      </div>
      <div className="flex gap-2">
        <div className="w-20 h-9 rounded-lg bg-gray-200" />
        <div className="w-32 h-9 rounded-lg bg-gray-200" />
      </div>
    </div>
    {/* KPI strip */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <KpiCardSkeleton accentClass="border-l-emerald-400" />
      <KpiCardSkeleton accentClass="border-l-amber-400" />
      <KpiCardSkeleton accentClass="border-l-rose-400" />
      <KpiCardSkeleton accentClass="border-l-indigo-400" />
    </div>
    {/* Table */}
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
        <Bar w="w-24" h="h-3" />
        <Bar w="w-16" h="h-3" />
        <Bar w="w-16" h="h-3" />
        <Bar w="w-16" h="h-3" />
      </div>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="px-4 py-3 border-b border-gray-50 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gray-200" />
          <div className="flex-1 space-y-2">
            <Bar w="w-1/3" h="h-3" />
            <Bar w="w-1/5" h="h-3" />
          </div>
          <Bar w="w-16" h="h-4" />
          <Bar w="w-12" h="h-4" />
        </div>
      ))}
    </div>
  </SkeletonWrap>
);

// ─── Home / Dashboard ─────────────────────────────────────────────────────
export const HomeSkeleton = () => (
  <SkeletonWrap testId="home-skeleton">
    {/* Greeting */}
    <div className="space-y-2">
      <Bar w="w-64" h="h-8" />
      <Bar w="w-72" h="h-3" />
    </div>
    {/* KPI grid */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <KpiCardSkeleton accentClass="border-l-emerald-400" />
      <KpiCardSkeleton accentClass="border-l-blue-400" />
      <KpiCardSkeleton accentClass="border-l-amber-400" />
      <KpiCardSkeleton accentClass="border-l-rose-400" />
    </div>
    {/* Two-col content area */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-4 space-y-3">
        <Bar w="w-32" h="h-4" />
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gray-200" />
              <Bar w="w-1/2" h="h-3" />
              <Bar w="w-16 ml-auto" h="h-3" />
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
        <Bar w="w-24" h="h-4" />
        <Bar w="w-full" h="h-32" />
      </div>
    </div>
  </SkeletonWrap>
);
